import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Truck,
  Plus,
  Phone,
  Navigation,
  CheckCircle2,
  Clock,
  Radio,
  Shield,
  Search,
  RefreshCw,
  FileText,
  Layers
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { api } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ROUTES } from '@/routes/paths';

interface DriverItem {
  _id: string;
  userId: { _id: string; username: string; display_name: string } | string;
  name: string;
  phone: string;
  email?: string;
  vehicleNumber: string;
  vehicleType: string;
  capacityDrums: number;
  licenseNumber?: string;
  status: 'available' | 'on_trip' | 'off_duty';
  currentLocation?: {
    latitude: number;
    longitude: number;
    speed: number;
    battery: number;
    address: string;
    lastPingAt: string;
  };
  activeDelivery?: {
    dispatchId: string;
    documentType: string;
    documentNumber: string;
    customerName: string;
    shippingAddress: string;
    contactPerson: string;
    phone: string;
    status: string;
    itemsSummary: string;
    startedAt: string;
  };
  deliveryHistory?: any[];
}

export function FleetManagementPage() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<DriverItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'available' | 'on_trip' | 'off_duty'>('all');
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Mid-route reassignment (client-requested: "in case kuch hota hai" —
  // vehicle breakdown, driver unwell — a dispatcher needs to hand an
  // in-progress delivery to a different driver).
  const [reassignFrom, setReassignFrom] = useState<DriverItem | null>(null);
  const [reassignToId, setReassignToId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleType, setVehicleType] = useState('Tata 407 (7 Drum Capacity)');
  const [capacityDrums, setCapacityDrums] = useState('7');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('Zynx@Driver2026');
  const [submitting, setSubmitting] = useState(false);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const res = await api.get<{ ok: boolean; drivers: DriverItem[] }>('/api/fleet/drivers');
      if (res?.data?.drivers) {
        setDrivers(res.data.drivers);
      }
    } catch (err: any) {
      toast.error('Failed to load fleet drivers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const handleCreateDriver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !vehicleNumber.trim()) {
      toast.error('Please enter Name, Phone and Vehicle Number.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/api/fleet/drivers', {
        name: name.trim(),
        phone: phone.trim(),
        vehicleNumber: vehicleNumber.trim().toUpperCase(),
        vehicleType,
        capacityDrums: Number(capacityDrums) || 7,
        licenseNumber: licenseNumber.trim(),
        username: username.trim() || undefined,
        password: password.trim() || undefined,
      });

      if (res?.data?.ok) {
        toast.success(`Driver ${name} registered! ZYNX login credentials generated.`);
        setIsAddModalOpen(false);
        // Reset form
        setName('');
        setPhone('');
        setVehicleNumber('');
        setLicenseNumber('');
        setUsername('');
        fetchDrivers();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create driver.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignFrom || !reassignToId) return;
    try {
      setReassigning(true);
      const res = await api.post(`/api/fleet/drivers/${reassignFrom._id}/reassign`, { toDriverId: reassignToId });
      if (res?.data?.ok) {
        toast.success(`Delivery reassigned to ${res.data.toDriver.name}.`);
        setReassignFrom(null);
        setReassignToId('');
        fetchDrivers();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to reassign delivery.');
    } finally {
      setReassigning(false);
    }
  };

  const filteredDrivers = drivers.filter((d) => {
    if (filter !== 'all' && d.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        d.name.toLowerCase().includes(q) ||
        d.vehicleNumber.toLowerCase().includes(q) ||
        d.phone.includes(q)
      );
    }
    return true;
  });

  const totalDrivers = drivers.length;
  const availableCount = drivers.filter((d) => d.status === 'available').length;
  const onTripCount = drivers.filter((d) => d.status === 'on_trip').length;
  const offDutyCount = drivers.filter((d) => d.status === 'off_duty').length;

  return (
    <div className="space-y-6 max-w-[1580px] mx-auto pb-16">
      <PageHeader
        icon={Truck}
        eyebrow="Dispatch"
        title="Fleet & Drivers"
        description="Drivers, vehicle assignments, live location and deliveries."
        actions={<>
          <Button variant="outline" onClick={() => navigate(ROUTES.liveTracking || '/live-radar')}>
            <Radio className="w-4 h-4" /> Live GPS Radar
          </Button>
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus className="w-4 h-4" /> Add Truck Driver
          </Button>
        </>}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-slate-500">Total Fleet Vehicles</span>
            <p className="text-2xl font-bold text-slate-900 mt-1">{totalDrivers}</p>
          </div>
          <div className="p-2.5 bg-slate-100 text-slate-700 rounded-lg">
            <Truck className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-success/25 bg-success/10 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-success-text">Available / Ready</span>
            <p className="text-2xl font-bold text-success-text mt-1">{availableCount}</p>
          </div>
          <div className="p-2.5 bg-success/10 text-success-text rounded-lg">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-info/25 bg-info/10 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-info-text">En Route / On Trip</span>
            <p className="text-2xl font-bold text-info-text mt-1">{onTripCount}</p>
          </div>
          <div className="p-2.5 bg-info/10 text-info-text rounded-lg">
            <Radio className="w-5 h-5 animate-pulse" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-warning/25 bg-warning/10 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-warning-text">Off Duty / Depot</span>
            <p className="text-2xl font-bold text-warning-text mt-1">{offDutyCount}</p>
          </div>
          <div className="p-2.5 bg-warning/10 text-warning-text rounded-lg">
            <Clock className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center flex-wrap gap-y-2 text-slate-600 dark:text-slate-400">
          {(['all', 'available', 'on_trip', 'off_duty'] as const).map((tab, idx) => {
            let IconComponent = Truck;
            if (tab === 'all') IconComponent = Layers;
            else if (tab === 'available') IconComponent = CheckCircle2;
            else if (tab === 'on_trip') IconComponent = Navigation;
            else if (tab === 'off_duty') IconComponent = Clock;

            const isActive = filter === tab;

            return (
              <div key={tab} className="flex items-center">
                {idx > 0 && (
                  <span className="text-slate-300 dark:text-slate-700 mx-3 select-none text-[11px] font-light">&lt;</span>
                )}
                <button
                  onClick={() => setFilter(tab)}
                  className={cn(
                    "inline-flex items-center gap-1.5 text-[13px] font-semibold transition-all duration-150 border-0 bg-transparent py-1 px-2.5 rounded-md hover:text-slate-900 dark:hover:text-slate-100",
                    isActive
                      ? 'text-info-text dark:text-info-text font-semibold bg-info/10 dark:bg-info/15'
                      : 'text-muted-foreground'
                  )}
                >
                  <IconComponent className={cn("w-4 h-4", isActive ? "text-info-text dark:text-info-text" : "text-slate-400")} />
                  <span className="capitalize">{tab.replace('_', ' ')}</span>
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search driver or truck..."
              className="w-full h-8 pl-8 pr-3 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-success/40"
            />
          </div>
          <button
            onClick={fetchDrivers}
            title="Refresh"
            className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Driver Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center p-12 bg-white rounded-xl border border-slate-200">
          <Spinner size="md" />
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="text-center p-12 bg-white rounded-xl border border-slate-200">
          <Truck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-700">No fleet drivers found.</p>
          <p className="text-xs text-slate-500 mt-1">Add your first driver or adjust your search filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrivers.map((driver) => {
            const isLive = driver.currentLocation?.lastPingAt && (Date.now() - new Date(driver.currentLocation.lastPingAt).getTime() < 180000);
            return (
              <div
                key={driver._id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition p-4 flex flex-col justify-between"
              >
                <div>
                  {/* Top Bar with Status Badge */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-lg bg-success/10 text-success-text flex items-center justify-center font-bold text-sm">
                        🚚
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-sm leading-tight">{driver.name}</h3>
                        <p className="text-[11px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3 text-slate-400" /> {driver.phone}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${
                        driver.status === 'on_trip'
                          ? 'bg-info/10 text-info-text border-info/25 animate-pulse'
                          : driver.status === 'available'
                          ? 'bg-success/10 text-success-text border-success/25'
                          : 'bg-slate-100 text-slate-600 border-slate-200'
                      }`}
                    >
                      {driver.status === 'on_trip' ? '● En Route' : driver.status}
                    </span>
                  </div>

                  {/* Vehicle Spec Card */}
                  <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs space-y-1 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Truck Number:</span>
                      <span className="font-bold font-mono text-success-text">{driver.vehicleNumber}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Vehicle Type:</span>
                      <span className="font-medium text-slate-700">{driver.vehicleType}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Max Capacity:</span>
                      <span className="font-medium text-slate-700">{driver.capacityDrums || 7} Drum Barrel</span>
                    </div>
                    {driver.licenseNumber && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">License:</span>
                        <span className="font-mono text-slate-600 text-[11px]">{driver.licenseNumber}</span>
                      </div>
                    )}
                  </div>

                  {/* Active Delivery Card if on trip */}
                  {driver.activeDelivery ? (
                    <div className="bg-info/10 p-2.5 rounded-lg border border-info/25 text-xs mb-3 space-y-1">
                      <div className="flex items-center justify-between text-info-text font-bold">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5 text-info-text" />
                          {driver.activeDelivery.documentNumber || 'Active Dispatch'}
                        </span>
                        <span className="text-[10px] bg-info/15 px-1.5 py-0.5 rounded font-mono">
                          {driver.activeDelivery.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-info-text font-semibold text-[11px] line-clamp-1">
                        🏢 {driver.activeDelivery.customerName}
                      </p>
                      <p className="text-info-text text-[10.5px] line-clamp-1">
                        📍 {driver.activeDelivery.shippingAddress}
                      </p>
                      <button
                        type="button"
                        onClick={() => setReassignFrom(driver)}
                        className="w-full mt-1 py-1 rounded-md bg-white border border-info/25 text-info-text text-[10.5px] font-bold hover:bg-info/10 transition"
                      >
                        Reassign to another driver
                      </button>
                    </div>
                  ) : (
                    <div className="bg-success/10 p-2 rounded-lg border border-success/15 text-center text-[11px] text-success-text mb-3 flex items-center justify-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Ready for next dispatch assignment
                    </div>
                  )}

                  {/* Telemetry Status */}
                  <div className="text-[11px] text-slate-500 flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="flex items-center gap-1">
                      <Radio className={`w-3 h-3 ${isLive ? 'text-success-text' : 'text-slate-400'}`} />
                      {isLive ? 'Transmitting GPS' : 'Depot Stationary'}
                    </span>
                    <span className="font-mono text-slate-600">
                      {driver.currentLocation?.speed ? `${driver.currentLocation.speed} km/h` : '0 km/h'}
                    </span>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="grid grid-cols-2 gap-2 mt-4 pt-2 border-t border-slate-100">
                  <a
                    href={`tel:${driver.phone}`}
                    className="py-1.5 px-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-semibold flex items-center justify-center gap-1 transition"
                  >
                    <Phone className="w-3.5 h-3.5" /> Call Driver
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(ROUTES.liveTracking || '/live-radar')}
                    className="text-xs text-success-text border-success/25 hover:bg-success/10 flex items-center justify-center gap-1"
                  >
                    <Navigation className="w-3.5 h-3.5" /> Track on Radar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Driver Modal */}
      <Modal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Register New Truck Driver &amp; Vehicle"
        size="md"
      >
        <form onSubmit={handleCreateDriver} className="space-y-3.5">
          <div className="p-3 rounded-lg bg-success/10 border border-success/25 text-xs text-success-text flex items-start gap-2">
            <Shield className="w-4 h-4 text-success-text mt-0.5" />
            <div>
              <p className="font-bold">Automated ZYNX Driver Account</p>
              <p className="text-[11px] text-success-text mt-0.5">
                Registering a driver creates their ZYNX login so they can log into the Driver Delivery Portal on mobile.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Driver Full Name <span className="text-danger-text">*</span>
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ramesh Patil"
                required
                className="text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Mobile Number <span className="text-danger-text">*</span>
              </label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 9822012345"
                required
                className="text-xs font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Truck / Vehicle Number <span className="text-danger-text">*</span>
              </label>
              <Input
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                placeholder="e.g. MH 12 QW 4590"
                required
                className="text-xs font-mono uppercase"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Vehicle Type &amp; Model
              </label>
              <select
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-slate-300 text-xs bg-white focus:ring-2 focus:ring-success/40 focus:outline-none"
              >
                <option value="Tata 407 (7 Drum Capacity)">Tata 407 (7 Drum Capacity)</option>
                <option value="Eicher 14ft (12 Drum Capacity)">Eicher 14ft (12 Drum Capacity)</option>
                <option value="Bolero Maxi Truck (4 Drum Capacity)">Bolero Maxi Truck (4 Drum Capacity)</option>
                <option value="Bulk Oil Tanker (5000L)">Bulk Oil Tanker (5000L)</option>
                <option value="Tempo / Intra V30">Tempo / Intra V30</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Drum Barrel Capacity
              </label>
              <Input
                type="number"
                value={capacityDrums}
                onChange={(e) => setCapacityDrums(e.target.value)}
                placeholder="7"
                className="text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Driving License No.
              </label>
              <Input
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
                placeholder="e.g. MH12 20180012345"
                className="text-xs font-mono uppercase"
              />
            </div>
          </div>

          {/* Login Credentials Section */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2.5">
            <span className="text-xs font-bold text-slate-800">ZYNX App Login Access</span>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Username (Optional)</label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Auto-generated if blank"
                  className="text-xs font-mono"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">Default Password</label>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Zynx@Driver2026"
                  className="text-xs font-mono"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsAddModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" size="sm" className="bg-success-text hover:bg-success-text/90 text-on-primary" disabled={submitting}>
              {submitting ? (
                <span className="flex items-center gap-1">
                  <Spinner size="sm" /> Creating Driver...
                </span>
              ) : (
                'Save & Create Driver Account'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Mid-Route Reassignment Modal */}
      <Modal
        open={!!reassignFrom}
        onClose={() => {
          setReassignFrom(null);
          setReassignToId('');
        }}
        title="Reassign Delivery to Another Driver"
        size="sm"
      >
        {reassignFrom?.activeDelivery && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-info/10 border border-info/25 text-xs text-info-text">
              <p className="font-bold">{reassignFrom.activeDelivery.documentNumber || 'Active Delivery'}</p>
              <p className="text-info-text mt-0.5">🏢 {reassignFrom.activeDelivery.customerName}</p>
              <p className="text-info-text mt-0.5">Currently with <b>{reassignFrom.name}</b> ({reassignFrom.vehicleNumber})</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Hand off to <span className="text-danger-text">*</span>
              </label>
              {drivers.filter((d) => d._id !== reassignFrom._id && d.status === 'available').length === 0 ? (
                <p className="text-xs text-slate-500 italic">No other drivers are currently available.</p>
              ) : (
                <select
                  value={reassignToId}
                  onChange={(e) => setReassignToId(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-info/40 focus:outline-none"
                >
                  <option value="">Select an available driver…</option>
                  {drivers
                    .filter((d) => d._id !== reassignFrom._id && d.status === 'available')
                    .map((d) => (
                      <option key={d._id} value={d._id}>
                        {d.name} — {d.vehicleNumber}
                      </option>
                    ))}
                </select>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setReassignFrom(null);
                  setReassignToId('');
                }}
                disabled={reassigning}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-primary-dim hover:bg-primary-dim text-white"
                onClick={handleReassign}
                disabled={reassigning || !reassignToId}
              >
                {reassigning ? (
                  <span className="flex items-center gap-1">
                    <Spinner size="sm" /> Reassigning...
                  </span>
                ) : (
                  'Confirm Reassignment'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
